import type { PropsWithChildren } from 'react'
import React, { useEffect, useState } from 'react'
import { AuthProvider, OTLContextProvider } from '@/auth'
import { WebFileRepository } from '@/file'
import { MiscellaneousProvider } from '@/miscellaneous'
import { PreloadedMoteConversationStateProvider } from '@/mote'
import { StorageDataMigrator } from '@/utils'
import { FlightRecorderProvider } from '@mote/helpers/contexts'
import { LocaleProvider } from '@mote/l10n/providers/LocaleProvider'
import {
  FileProvider,
  InMemoryMotePlaybackLocationProvider
} from '@mote/recipes'
import { ThemeProvider } from '@mote/ui/system'
import { UserAgentProvider } from '@quentin-sommer/react-useragent'
import { useRemovePreloader } from './useRemovePreloader'

export function MoteEntryPointProviders(props: PropsWithChildren) {
  const [loading, setLoading] = useState(true)
  const [fileRepository] = useState(() => new WebFileRepository())
  const [storageMigrator] = useState(() => new StorageDataMigrator())
  useRemovePreloader()

  useEffect(() => {
    storageMigrator
      .checkAndUpdateStorageVersion()
      .then(() => setLoading(false))
      .catch(() => setLoading(false))
  }, [])

  if (loading) {
    return null
  }

  if (
    window.location.host.includes('justmote.me') ||
    (window.location.host.includes('mote.com') &&
      !window.location.host.includes('www') &&
      !window.location.host.includes('dev') &&
      !window.location.host.includes('stg'))
  ) {
    const newDomainToRedirectTo =
      window.location.host === 'support.justmote.me'
        ? 'https://support.mote.com'
        : 'https://www.mote.com'
    const url = `${newDomainToRedirectTo}${window.location.pathname}${window.location.search}${window.location.hash}`
    window.location.replace(url)
    return null
  }

  return (
    <MiscellaneousProvider>
      <InMemoryMotePlaybackLocationProvider>
        <FileProvider repository={fileRepository}>
          <PreloadedMoteConversationStateProvider>
            <ThemeProvider>
              <UserAgentProvider ua={window.navigator.userAgent}>
                <FlightRecorderProvider>
                  <AuthProvider>
                    <OTLContextProvider>
                      <LocaleProvider>{props.children}</LocaleProvider>
                    </OTLContextProvider>
                  </AuthProvider>
                </FlightRecorderProvider>
              </UserAgentProvider>
            </ThemeProvider>
          </PreloadedMoteConversationStateProvider>
        </FileProvider>
      </InMemoryMotePlaybackLocationProvider>
    </MiscellaneousProvider>
  )
}
